// Fleet Compliance tab (fleet-2): every survey, ship certificate, crew certificate, open ISM finding, drill, RA review and SOP review
// that is overdue or falls due in the next 90 days, on every vessel. Plain JS (tests/fleet-2.test.mjs): the Fleet page passes the rows
// it already reads (fetchRows display names for documents / crew certificates, raw rows for the rest) and shows / exports what comes out.
import { daysTo, windowOf, worstFirst } from './fleetCore.js'
import { expiryStatus } from './expiry.js'

export const TYPE_LABEL = { survey: 'Survey', ship_cert: 'Ship certificate', crew_cert: 'Crew certificate', ism: 'ISM finding', drill: 'Drill', ra: 'RA review', sop: 'SOP review' }
export const TYPES = Object.keys(TYPE_LABEL)
export const WHEN = [['all', 'All'], ['overdue', 'Overdue'], [30, 'Due ≤ 30 d'], [60, 'Due ≤ 60 d'], [90, 'Due ≤ 90 d']]
export const HORIZON = 90

const val = v => {
  if (v == null) return ''
  if (typeof v === 'object' && !Array.isArray(v)) return String(v.value || v.name || v.label || '')
  if (Array.isArray(v)) return v.length ? val(v[0]) : ''
  return String(v)
}
const isoOf = v => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val(v)); return m ? m[0] : null }
const gone = s => /archiv|superseded|withdrawn|rejected/i.test(String(s || ''))
const crewName = c => (c ? [c.first_name, c.last_name].filter(Boolean).join(' ') || c.name || '' : '')

// "overdue 12 d" / "today" / "in 22 d" / "no due date"
export const whenOf = it => it.whenText || daysText(it.days)
export function daysText(days) {
  if (days == null) return 'no due date'
  if (days < 0) return 'overdue ' + -days + ' d'
  return days === 0 ? 'today' : days === 1 ? 'tomorrow' : 'in ' + days + ' d'
}

// one vessel -> its items. d: { docs, crewDocs, crew, drills (drillsNeedingAttention), ras, ism (open findings), sops (manuals, doc_kind sop) }
// see(module) -> the module is in this vessel's plan. today: 'YYYY-MM-DD'
export function vesselCompliance(v, d, { today, see = () => true } = {}) {
  const out = []
  const add = (type, name, date, detail, extra = {}) => {
    const days = daysTo(date, today)
    const { keep, ...more } = extra
    if (days == null && !keep) return
    const status = days == null ? 'open' : days < 0 ? 'overdue' : 'due'
    if (status === 'due' && !windowOf(days)) return                     // more than 90 days away
    out.push({ vid: v.id, vessel: v.vessel_name, type, typeLabel: TYPE_LABEL[type], name: name || TYPE_LABEL[type], detail: detail || '', date: isoOf(date), days, status, window: status === 'due' ? windowOf(days) : null, ...more })
  }
  if (see('documents')) for (const r of d.docs || []) {
    if (gone(val(r['Status']))) continue
    const t = val(r['Title']) || 'Document', type = val(r['Document Type'])
    add('ship_cert', t, r['Expiry Date'], [type, expiryStatus(r['Expiry Date'], today).expired ? 'expired' : 'expires'].filter(Boolean).join(' · '))   // the shared rule (src/lib/expiry.js)
    if (isoOf(r['Survey Due'])) add('survey', t, r['Survey Due'], 'survey due' + (type ? ' · ' + type : ''))
  }
  if (see('crew')) {
    const byId = new Map((d.crew || []).map(c => [String(c.id), c]))
    for (const r of d.crewDocs || []) {
      if (gone(val(r['Status']))) continue
      const who = crewName(byId.get(String(val(r['Crew Record']))))
      add('crew_cert', val(r['Title']) || val(r['Type']) || 'Certificate', r['Expiry Date'], [who, val(r['Type'])].filter(Boolean).join(' · '))
    }
  }
  if (see('ism_audits')) for (const f of d.ism || []) {
    if (f.status === 'closed') continue
    add('ism', [f.ref, f.description].filter(Boolean).join(' ').slice(0, 140) || 'ISM finding', f.due_date,
      [f.grade === 'major_nc' ? 'Major NC' : f.grade === 'observation' ? 'Observation' : f.grade === 'coc' ? 'Condition of class' : f.grade === 'deficiency' ? 'Deficiency' : 'NC',
        f.status === 'action_taken' ? 'awaiting verification' : 'open', f.element ? 'ISM ' + f.element : ''].filter(Boolean).join(' · '), { keep: true })
  }
  if (see('checklists')) for (const x of d.drills || []) {
    // an overdue drill has no due date: "days" is only an estimate for sorting (months late x 30); the text says when it was last done
    if (x.status === 'overdue') out.push({ vid: v.id, vessel: v.vessel_name, type: 'drill', typeLabel: TYPE_LABEL.drill, name: x.label + ' drill', detail: 'last done ' + (x.monthsAgo != null ? x.monthsAgo + ' months ago' : '-') + ' (every ' + x.freqMonths + ' mo)', date: null,
      days: x.monthsAgo != null ? -30 * Math.max(1, x.monthsAgo - x.freqMonths) : -1, approx: true, whenText: x.monthsAgo != null ? 'last done ' + x.monthsAgo + ' mo ago' : 'overdue', status: 'overdue', window: null })
    else if (x.status === 'due') { const end = monthEnd(today); add('drill', x.label + ' drill', end, 'due this month') }
  }
  if (see('risk')) for (const r of d.ras || []) {
    if (gone(r.status)) continue
    add('ra', [r.ra_number, r.activity].filter(Boolean).join(' ') || 'Risk assessment', r.next_review_due, 'review due')
  }
  if (see('manuals')) for (const m of d.sops || []) {
    if (m.archived_at || gone(m.status)) continue
    add('sop', [m.doc_number, m.title].filter(Boolean).join(' ') || 'SOP', m.review_due, 'review due')
  }
  return out
}
const monthEnd = today => { const [y, m] = String(today).split('-').map(Number); return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10) }

export function fleetCompliance(fleet, data, opts) {
  return (fleet || []).flatMap(v => (data[v.id] ? vesselCompliance(v, data[v.id], { ...opts, see: opts && opts.seeFor ? opts.seeFor(v) : undefined }) : []))
}

// when: 'all' (overdue + open + due within 90 d) | 'overdue' | 30 | 60 | 90 (due within N days, not yet late); type: 'all' | a TYPES key
export function filterCompliance(items, { when = 'all', type = 'all' } = {}) {
  return (items || []).filter(it => (type === 'all' || it.type === type) && (
    when === 'all' ? true : when === 'overdue' ? it.status === 'overdue' : it.status === 'due' && it.days <= Number(when)))
}

// sort: key 'days' (soonest / most overdue first, no due date last) | 'vessel' | 'type' | 'name' | 'status'; dir 1 / -1.
// rank: vessel id -> position (worst readiness first), so "vessel" sorts like the Overview.
const STATUS_ORDER = { overdue: 0, due: 1, open: 2 }
export function sortCompliance(items, key = 'days', dir = 1, rank = {}) {
  const d = x => (x.days == null ? Infinity : x.days)
  const by = {
    days: (a, b) => d(a) - d(b),
    vessel: (a, b) => (rank[a.vid] ?? 999) - (rank[b.vid] ?? 999) || String(a.vessel).localeCompare(String(b.vessel)),
    type: (a, b) => TYPES.indexOf(a.type) - TYPES.indexOf(b.type),
    name: (a, b) => String(a.name).localeCompare(String(b.name)),
    status: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
  }[key] || ((a, b) => d(a) - d(b))
  // an item without a due date stays last whichever way the dates are sorted
  const nul = (a, b) => (key === 'days' ? (a.days == null) - (b.days == null) : 0)
  return [...(items || [])].sort((a, b) => nul(a, b) || dir * by(a, b) || d(a) - d(b) || String(a.name).localeCompare(String(b.name)))
}

// grouped by vessel, in the Overview order (worst readiness first); each group keeps the items' order
export function groupByVessel(items, fleet, health = {}) {
  const order = worstFirst(fleet || [], health)
  return order.map(v => {
    const its = (items || []).filter(it => String(it.vid) === String(v.id))
    return { vid: v.id, vessel: v.vessel_name, items: its, overdue: its.filter(i => i.status === 'overdue').length, due: its.filter(i => i.status === 'due').length, open: its.filter(i => i.status === 'open').length }
  }).filter(g => g.items.length)
}

export const counts = items => ({
  overdue: (items || []).filter(i => i.status === 'overdue').length,
  d30: (items || []).filter(i => i.status === 'due' && i.days <= 30).length,
  d60: (items || []).filter(i => i.status === 'due' && i.days > 30 && i.days <= 60).length,
  d90: (items || []).filter(i => i.status === 'due' && i.days > 60).length,
  open: (items || []).filter(i => i.status === 'open').length,
})
export const statusText = it => (it.status === 'overdue' ? (it.type === 'ship_cert' || it.type === 'crew_cert' ? 'Expired' : 'Overdue') : it.status === 'open' ? 'Open' : 'Due ≤ ' + it.window + ' d')

// the Excel sheet: [[header...], [values...]]
export const COLUMNS = ['Vessel', 'Type', 'Item', 'Detail', 'Due date', 'Days', 'Status']
export function complianceTable(items) {
  return [COLUMNS, ...(items || []).map(it => [it.vessel, it.typeLabel, it.name, it.detail, it.date || '', it.days == null || it.approx ? '' : it.days, statusText(it)])]
}
